import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { useNavigate } from 'react-router-dom'

const NAVY  = '#0B1F3A'
const TEAL  = '#2DD4B0'
const MUTED = '#6B7280'
const BORDER= '#E5E7EB'
const BG    = '#F8FAFC'

const inp = {
  width: '100%', border: '1.5px solid ' + BORDER, borderRadius: '10px',
  padding: '11px 14px', fontSize: '14px', outline: 'none',
  fontFamily: 'Space Grotesk, -apple-system, sans-serif',
  color: '#111827', background: '#fff', boxSizing: 'border-box',
  transition: 'border-color 0.15s',
}

function PwInput({ value, onChange, onKeyDown, placeholder }) {
  const [show, setShow] = useState(false)
  return (
    <div style={{ position: 'relative' }}>
      <input type={show ? 'text' : 'password'} value={value}
        onChange={onChange} onKeyDown={onKeyDown}
        placeholder={placeholder || '••••••••'}
        style={{ ...inp, paddingRight: '52px' }}
        onFocus={e => e.target.style.borderColor = TEAL}
        onBlur={e => e.target.style.borderColor = BORDER} />
      <button type="button" onClick={() => setShow(s => !s)}
        style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: MUTED, fontSize: '11.5px', fontWeight: 600, padding: '2px', fontFamily: 'inherit' }}>
        {show ? 'Hide' : 'Show'}
      </button>
    </div>
  )
}

function ResetPassword() {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm]   = useState('')
  const [loading, setLoading]   = useState(false)
  const [done, setDone]         = useState(false)
  const [error, setError]       = useState('')
  const [ready, setReady]       = useState(false)

  useEffect(() => {
    supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setReady(true)
    })
  }, [])

  const handleReset = async () => {
    if (!password || password.length < 6) { setError('Password must be at least 6 characters'); return }
    if (password !== confirm) { setError('Passwords do not match'); return }
    setLoading(true); setError('')
    const { error: err } = await supabase.auth.updateUser({ password })
    if (err) { setError(err.message); setLoading(false) }
    else { setDone(true); setLoading(false); setTimeout(() => navigate('/login'), 3000) }
  }

  return (
    <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', fontFamily: 'Space Grotesk, -apple-system, sans-serif' }}>
      <style>{`@keyframes rp-spin { to { transform: rotate(360deg) } }`}</style>
      <div style={{ background: '#fff', borderRadius: '20px', width: '100%', maxWidth: '400px', overflow: 'hidden', boxShadow: '0 8px 40px rgba(11,31,58,0.12)', border: '1px solid ' + BORDER }}>

        {/* Header */}
        <div style={{ background: NAVY, padding: '28px 32px', textAlign: 'center' }}>
          <div style={{ fontSize: '22px', fontWeight: 800, color: '#fff', letterSpacing: '-0.5px', marginBottom: '4px' }}>PageConnect</div>
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>Set new password</div>
        </div>

        <div style={{ padding: '32px' }}>
          {done ? (
            /* ── Success ── */
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(45,212,176,0.1)', border: '2px solid rgba(45,212,176,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <div style={{ fontSize: '20px', fontWeight: 800, color: '#111827', marginBottom: '8px' }}>Password updated!</div>
              <div style={{ fontSize: '13px', color: MUTED, marginBottom: '24px', lineHeight: 1.6 }}>
                Your password has been changed. Redirecting to login...
              </div>
              <button onClick={() => navigate('/login')}
                style={{ width: '100%', padding: '13px', borderRadius: '12px', background: NAVY, border: 'none', fontSize: '14px', fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
                Go to login
              </button>
            </div>

          ) : !ready ? (
            /* ── Validating ── */
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(45,212,176,0.08)', border: '1px solid rgba(45,212,176,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="1.8" strokeLinecap="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
              </div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '6px' }}>Validating reset link...</div>
              <div style={{ fontSize: '12.5px', color: MUTED, marginBottom: '16px', lineHeight: 1.6 }}>
                If this takes too long, please request a new reset link.
              </div>
              <button onClick={() => navigate('/forgot-password')}
                style={{ fontSize: '13px', fontWeight: 600, color: NAVY, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                Request new link →
              </button>
            </div>

          ) : (
            /* ── Form ── */
            <>
              <div style={{ fontSize: '20px', fontWeight: 800, color: '#111827', marginBottom: '6px' }}>New password</div>
              <div style={{ fontSize: '13px', color: MUTED, marginBottom: '24px' }}>Choose a strong password for your account</div>

              {error && (
                <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', padding: '10px 12px', marginBottom: '16px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, marginTop: '1px' }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <span style={{ fontSize: '12.5px', color: '#B91C1C' }}>{error}</span>
                </div>
              )}

              <div style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '6px' }}>New password</label>
                <PwInput value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '6px' }}>Confirm password</label>
                <PwInput value={confirm} onChange={e => setConfirm(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleReset()} placeholder="Repeat password" />
              </div>

              <button onClick={handleReset} disabled={loading}
                style={{ width: '100%', padding: '13px', borderRadius: '12px', background: loading ? BORDER : NAVY, border: 'none', fontSize: '14px', fontWeight: 700, color: loading ? '#9CA3AF' : '#fff', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                {loading && <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'rp-spin 0.7s linear infinite' }} />}
                {loading ? 'Updating...' : 'Update password'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default ResetPassword